// config: fetch this page's slots from our API.
// The server does the work: it has already matched the page, picked the live sponsor of each slot,
// built the click-redirect links and applied pauses, suspensions and the kill switch. A slot that
// should show nothing is simply not in the list. The browser (and the CDN) cache the answer for 60
// seconds, except for the site's owner, who sees each change at once.

/** @typedef {{ brand: string, text?: string, cta?: string, href: string, logo?: string, image?: string, color?: string, code?: string }} Sponsor */
/** @typedef {{ text: string, href: string, via?: string }} Placeholder  "Sponsor this spot · $49/month" to the booking page; `via` links "via Sponsor.st" (Pro can hide it) */
/** @typedef {{ width?: number | 'match', height?: 'slim' | 'tall', align?: 'left' | 'center' | 'right', text?: 'center', stack?: boolean }} Style */
/** @typedef {{ e: number, name: string, text?: string, logo?: string, color?: string, href?: string, amount?: string, clicks?: number }} Row  one leaderboard entry, in rank order */
/**
 * @typedef {object} Board  a leaderboard slot: drawn by the lazy board chunk (b.vN.js), not a format
 * @property {Row[]} rows
 * @property {{ text: string, href: string }} [cta]  "Take #1 for $X" or "Be #1 · from $5", to the bid page
 * @property {string} [via]  "via Sponsor.st"
 * @property {number} [views]  this period's board views, when the owner shows stats
 */
/**
 * @typedef {object} Slot
 * @property {string} id  the slot's public id
 * @property {string} [format]  one of the launch formats (none for a board)
 * @property {Board} [board]  a leaderboard, instead of a sponsor or placeholder
 * @property {string} [selector]  where it goes (not for pinned formats)
 * @property {'before' | 'after' | 'prepend' | 'append'} [position]  next to the element, or inside it first/last
 * @property {Style} [style]
 * @property {Sponsor} [sponsor]  the live sponsor, if booked
 * @property {Placeholder} [placeholder]  shown when there is no sponsor
 */
/** @typedef {{ mode?: 'light' | 'dark', accent?: string, radius?: 'square' }} Look  the owner's overrides; empty means "match the site" */
/** @typedef {{ slots: Slot[], look: Look }} Config */

/**
 * @param {string} api  the embed API, e.g. https://cdn.sponsor.st/x
 * @param {string} key  the site's embed key
 * @param {string} path  this page, see pagePath()
 * @param {boolean} [fresh]  past every cache (the CDN's too: a URL of its own), for the owner
 * @returns {Promise<Config | null>}  null: nothing to show (unknown site, rate limited, server trouble),
 *   so nothing is drawn and no ping is sent: a visitor we couldn't serve never counts as a miss
 */
export async function load(api, key, path, fresh = false) {
    // d: this domain, so the edge cache keeps each domain's answer apart (the server checks it's the one asking)
    const url = `${api}/${encodeURIComponent(key)}?path=${encodeURIComponent(path)}&d=${location.hostname}`;
    const res = await fetch(fresh ? `${url}&f=${Date.now()}` : url, { credentials: 'omit', cache: fresh ? 'no-store' : 'default' });
    if (!res.ok) return null;
    const { slots, look } = await res.json();
    return { slots: Array.isArray(slots) ? slots : [], look: look || {} };
}

/** This page's path: "/post", "/post/" and "/post/index.html" are the same page. Case is kept. */
export function pagePath() {
    return location.pathname.replace(/\/index\.html?$/, '/').replace(/\/+$/, '') || '/';
}
